import { useMemo } from 'react'
import { useAccounts } from '@/api/accounts'
import { useSession } from '@/auth/useSession'
import { Page } from '@/components/layout/Page'
import { EmptyState } from '@/components/lobby/EmptyState'
import { ErrorCard } from '@/components/lobby/ErrorCard'
import { Greeting } from '@/components/lobby/Greeting'
import { LobbyLoading } from '@/components/lobby/LobbyLoading'
import { TenantList } from '@/components/lobby/TenantList'
import { sortAccounts } from '@/components/lobby/sortAccounts'
import { UserBadge } from '@/components/lobby/UserBadge'

/**
 * Authenticated landing — the workspace picker.
 *
 * Two shapes, not one with holes in it: a failure or an empty membership
 * set replaces the greeting with an outcome screen, because "Welcome back,
 * pick a workspace" above a "we couldn't load your workspaces" panel reads
 * as two pages stacked. Everything else composes greeting → list, with the
 * account menu up in the chrome rather than in the column — it isn't part of
 * picking a workspace.
 *
 * A single membership still renders the picker, with one row in it. The page
 * shows what you have and waits for a click; it does not decide for you.
 *
 * There is no search: memberships run to a handful, where a filter costs a
 * control and buys nothing.
 *
 * `RequireAuth` gatekeeps this route, so a session is present by the time
 * `useAccounts()` fires.
 */
export function LobbyPage() {
  const { email, displayName } = useSession()
  const { data, isLoading, error, refetch } = useAccounts()

  const accounts = useMemo(() => data ?? [], [data])
  const sorted = useMemo(() => sortAccounts(accounts), [accounts])

  // The session outlives whatever the accounts call did, so the account menu
  // belongs on every branch — including the failures. A user who lands on
  // "couldn't load your workspaces" still needs a way out, and dropping the
  // avatar there is what strands them.
  const identity = <UserBadge />

  if (error) {
    return (
      <Page identity={identity}>
        <ErrorCard onRetry={() => void refetch()} />
      </Page>
    )
  }

  if (!isLoading && accounts.length === 0) {
    return (
      <Page identity={identity}>
        <EmptyState />
      </Page>
    )
  }

  return (
    <Page variant="lobby" identity={identity}>
      <div className="navslot" />
      <Greeting email={email} displayName={displayName} />

      <div id="lobby-body">
        {isLoading ? <LobbyLoading /> : <TenantList accounts={sorted} />}
      </div>
    </Page>
  )
}
